import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X, ChevronDown, Check, Search, PhoneCall, MapPin } from 'lucide-react';
import Alert from '../../../common/Alerts/Alert';
import API_ENDPOINTS from '../../../config/apiEndpoints';

const AddAppointment = ({ onClose, onSubmit, appointmentData, isEdit }) => {
    // API Configuration
    const API_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const Sales_Activity = `${WCF_BASE}/UserCRMCampaign/Service/ToDoService.asmx/GetCustomUserTaskListByUserId`;
    const FOR_OWNER = `${API_BASE}${API_ENDPOINTS.APPOINTMENTS.GET_USER_HIRERARCHY_LIST}`;
    const Get_Collabrator = `${API_BASE}${API_ENDPOINTS.APPOINTMENTS.GET_USERLIST_PHYSICALAPP_VIEW}`;
    const ADD_TASK_APPOINTMENT = `${API_BASE}/UserCRM/InsertNewTaskOrAppointment_latest`;
    const RELATED_TO = `${API_BASE}/UserCRM/RelatedToSuggetion`;

    const userId = localStorage.getItem('userId') || '34594';

    const formatForDateTimeLocal = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    };

    const formatDateTimeDisplay = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        try {
            const d = new Date(dateTimeStr);
            if (isNaN(d.getTime())) return "";
            const monthsAbbr = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const datePart = `${d.getDate().toString().padStart(2, '0')}-${monthsAbbr[d.getMonth()]}-${d.getFullYear()}`;
            const timePart = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
            return `${datePart} ${timePart}`;
        } catch (e) { return ""; }
    };

    const getLocalISO = () => {
        const now = new Date();
        const tzOffset = now.getTimezoneOffset() * 60000;
        return (new Date(now - tzOffset)).toISOString().slice(0, 16);
    };

    const now = new Date();
    const future = new Date(now.getTime() + 60 * 60 * 1000); // +1 hour

    const defaultData = {
        title: `NewAppointment_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`,
        salesActivity: '8', // Default to Appointment type
        description: '',
        remarks: '',
        startDateTime: formatForDateTimeLocal(now),
        endDateTime: formatForDateTimeLocal(future),
        where: '',
        latitude: '',
        longitude: '',
        completeAddress: '',
        owner: userId,
        collaborators: []
    };

    const [formData, setFormData] = useState(defaultData);
    const [salesActivityOptions, setSalesActivityOptions] = useState([]);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [collabOptions, setCollabOptions] = useState([]);
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCollaborators, setShowCollaborators] = useState(false);

    // Related To State
    const [entityType, setEntityType] = useState('lead');
    const [entityName, setEntityName] = useState('');
    const [entityId, setEntityId] = useState('0');
    const [relatedSuggestions, setRelatedSuggestions] = useState([]);
    const [showRelatedDropdown, setShowRelatedDropdown] = useState(false);
    const [associatedSearch, setAssociatedSearch] = useState('');
    const [showAssociatedDropdown, setShowAssociatedDropdown] = useState(false);

    const whereInputRef = useRef(null);
    const googleAutocompleteRef = useRef(null);

    // Alert State
    const [alertConfig, setAlertConfig] = useState({
        show: false,
        type: 'success',
        title: 'Success',
        message: '',
        onConfirm: null
    });

    // Edit Mode Pre-fill
    useEffect(() => {
        if (isEdit && appointmentData) {
            setFormData({
                id: appointmentData.id || appointmentData.TaskHistoryId,
                title: appointmentData.taskTitle || appointmentData.title || '',
                description: appointmentData.description || '',
                remarks: appointmentData.Remarks || appointmentData.remarks || '',
                startDateTime: formatForDateTimeLocal(appointmentData.startDate || appointmentData.startDateTime),
                endDateTime: formatForDateTimeLocal(appointmentData.endDate || appointmentData.endDateTime),
                where: appointmentData?.address || '',
                latitude: appointmentData.latitude || '',
                longitude: appointmentData.longitude || '',
                completeAddress: appointmentData?.completeAddress || '',
                owner: appointmentData.AgentId || appointmentData.AssignedTo || appointmentData.owner || userId,
                collaborators: appointmentData.collaborators || (appointmentData.CollabratoerUserIds ? appointmentData.CollabratoerUserIds.split(/[#,]/) : [])
            });
            setEntityId(appointmentData.EntityId || appointmentData.leadId || appointmentData.enquiryId || appointmentData.id || '0');
            setEntityName(appointmentData.EntityName || appointmentData.entityName || appointmentData.name || '');
            setEntityType(appointmentData.EntityType || appointmentData.entityType || 'lead');
        } else if (appointmentData) {
            // Even if not edit, we might have lead info passed (e.g. adding from lead page)
            const eid = appointmentData.EntityId || appointmentData.leadId || appointmentData.enquiryId || appointmentData.id;
            if (eid) setEntityId(eid);
            const ename = appointmentData.EntityName || appointmentData.entityName || appointmentData.name;
            if (ename) setEntityName(ename);
            const etype = appointmentData.EntityType || appointmentData.entityType;
            if (etype) setEntityType(etype);
        }
    }, [isEdit, appointmentData]);

    // Fetch Logic
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                // Fetch Sales Activity
                const activityRes = await fetch(Sales_Activity, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ strTimeZone: "India Standard Time", userid: userId })
                });
                if (activityRes.ok) {
                    const data = await activityRes.json();
                    setSalesActivityOptions(data.d || []);
                }

                // Fetch Owners
                const ownerRes = await fetch(FOR_OWNER, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(userId, 10) } })
                });
                if (ownerRes.ok) {
                    const result = await ownerRes.json();
                    const rawData = result.Details?.data || result.Details || result.d || [];
                    let parsed = Array.isArray(rawData) ? rawData : [];
                    if (typeof rawData === 'string') { try { parsed = JSON.parse(rawData); } catch(e){} }
                    setOwnerOptions(Array.isArray(parsed) ? parsed : []);
                }

                // Fetch Collaborators
                const collabRes = await fetch(Get_Collabrator, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(userId, 10) } })
                });
                if (collabRes.ok) {
                    const result = await collabRes.json();
                    const rawData = result.Details?.data || result.Details || result.d || [];
                    let parsed = Array.isArray(rawData) ? rawData : [];
                    if (typeof rawData === 'string') { try { parsed = JSON.parse(rawData); } catch(e){} }
                    setCollabOptions(Array.isArray(parsed) ? parsed : []);
                }
            } catch (err) {
                console.error("Error fetching form options:", err);
            }
        };

        fetchOptions();
        initGoogleMaps();
    }, []);

    // Google Maps Logic
    const initGoogleMaps = () => {
        const scriptId = 'google-maps-script';
        const isGoogleMapsReady = () => window.google && window.google.maps && window.google.maps.places;

        if (isGoogleMapsReady()) {
            setScriptsLoaded(true);
            return;
        }

        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAO-dLMQIXYLvJeig0dbjeEqytvPg6EMd8&libraries=places&loading=async&callback=initGoogleMapsCallback`;
            script.async = true;
            script.defer = true;
            window.initGoogleMapsCallback = () => setScriptsLoaded(true);
            document.body.appendChild(script);
        } else {
            const checkInterval = setInterval(() => {
                if (isGoogleMapsReady()) {
                    setScriptsLoaded(true);
                    clearInterval(checkInterval);
                }
            }, 100);
            setTimeout(() => clearInterval(checkInterval), 10000);
        }
    };

    useEffect(() => {
        let autocompleteListener = null;

        if (scriptsLoaded) {
            const initAutocomplete = () => {
                if (!whereInputRef.current || !window.google?.maps?.places) return false;

                googleAutocompleteRef.current = new window.google.maps.places.Autocomplete(whereInputRef.current, {
                    types: ['geocode', 'establishment'],
                });

                autocompleteListener = googleAutocompleteRef.current.addListener('place_changed', () => {
                    const place = googleAutocompleteRef.current.getPlace();
                    const address = place.formatted_address || place.name;
                    setFormData(prev => ({
                        ...prev,
                        where: place.name || address,
                        latitude: place.geometry?.location?.lat() || '',
                        longitude: place.geometry?.location?.lng() || ''
                    }));
                });
                return true;
            };

            // Try immediately
            if (!initAutocomplete()) {
                // If ref not ready, poll for a bit
                const pollInterval = setInterval(() => {
                    if (initAutocomplete()) clearInterval(pollInterval);
                }, 100);
                setTimeout(() => clearInterval(pollInterval), 5000);
            }
        }

        return () => {
            if (autocompleteListener && window.google?.maps?.event) {
                window.google.maps.event.removeListener(autocompleteListener);
            }
        };
    }, [scriptsLoaded]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.related-search-container')) {
                setShowRelatedDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchRelatedToSuggestions = async (text, type) => {
        if (!text || text.length < 2) {
            setRelatedSuggestions([]);
            return;
        }
        try {
            const response = await fetch(RELATED_TO, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: "-2295521862261168",
                    Details: { UserId: parseInt(userId), FilterText: text, TaskType: type.toLowerCase() }
                })
            });
            if (response.ok) {
                const result = await response.json();
                const data = result.Details || result.d || [];
                setRelatedSuggestions(typeof data === 'string' ? JSON.parse(data) : data);
            }
        } catch (err) { console.error(err); }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Success Helper
    const showOperationSuccess = (message) => {
        showAlert('success', 'Success', message, () => {
            if (onSubmit) onSubmit(formData);
            if (onClose) onClose();
        });
    };

    const showAlert = (type, title, message, onConfirm = null) => {
        setAlertConfig({
            show: true,
            type: type === 'error' ? 'error' : 'success',
            title,
            message,
            onConfirm
        });
    };

    const handleAlertClose = () => {
        const confirmCallback = alertConfig.onConfirm;
        setAlertConfig(prev => ({ ...prev, show: false }));
        if (confirmCallback) confirmCallback();
    };

    const handleCollaboratorToggle = (collaborator) => {
        setFormData(prev => ({
            ...prev,
            collaborators: prev.collaborators.includes(collaborator)
                ? prev.collaborators.filter(c => c !== collaborator)
                : [...prev.collaborators, collaborator]
        }));
    };
    const handleReset = () => {
        const n = new Date();
        const f = new Date(n.getTime() + 60 * 60 * 1000);
        setFormData({
            title: `NewAppointment_${n.getFullYear()}${String(n.getMonth() + 1).padStart(2, '0')}${String(n.getDate()).padStart(2, '0')}${String(n.getHours()).padStart(2, '0')}${String(n.getMinutes()).padStart(2, '0')}${String(n.getSeconds()).padStart(2, '0')}`,
            salesActivity: '8',
            description: '',
            remarks: '',
            startDateTime: formatForDateTimeLocal(n),
            endDateTime: formatForDateTimeLocal(f),
            where: '',
            latitude: '',
            longitude: '',
            completeAddress: '',
            owner: userId,
            collaborators: []
        });
        setEntityName('');
        setEntityId('0');
        setEntityType('lead');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            showAlert('error', 'Error', "Please enter a title");
            return;
        }

        setIsSubmitting(true);
        try {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            // Supporting both single string format and split formats
            const sd = new Date(formData.startDateTime);
            const fd = new Date(formData.endDateTime);

            const formattedStartDate = `${sd.getDate().toString().padStart(2, '0')} ${months[sd.getMonth()]} ${sd.getFullYear()} ${sd.getHours().toString().padStart(2, '0')}:${sd.getMinutes().toString().padStart(2, '0')}:00`;
            const formattedEndDate = `${fd.getDate().toString().padStart(2, '0')} ${months[fd.getMonth()]} ${fd.getFullYear()} ${fd.getHours().toString().padStart(2, '0')}:${fd.getMinutes().toString().padStart(2, '0')}:00`;

            const leadId = appointmentData?.enquiryId || appointmentData?.leadId || appointmentData?.id || "0";

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    TimeZone: "India Standard Time",
                    insertUpdateTask: {
                        Id: isEdit ? (formData.id || 0) : 0,
                        Title: formData.title,
                        Description: formData.description || "",
                        Remarks: formData.remarks || "",
                        Address: formData.where || "",
                        StartDate: formattedStartDate,
                        EndDate: formattedEndDate,
                        AssignedTo: parseInt(formData.owner) || 34594,
                        BySMS: false,
                        CollabratoerUserIds: formData.collaborators.join('#'),
                        CompleteAddress: formData.completeAddress || formData.where || "",
                        CreatedBy: isEdit ? 0 : parseInt(userId),
                        DLTTemplateIDForNotify: "",
                        DLTTemplateIDForSMSReminder: "",
                        IsActive: true,
                        IsAppointment: true,
                        IsEmail: false,
                        IsSMS: false,
                        IsTask: false,
                        LeadId: parseInt(entityId || "0", 10),
                        Longitude: formData.longitude || "",
                        Latitude: formData.latitude || "",
                        NotifyMe: false,
                        OutcomesId: 0,
                        Status: "open",
                        TaskId: parseInt(formData.salesActivity) || 8,
                        TaskType: ""
                    }
                }
            };

            const response = await fetch(ADD_TASK_APPOINTMENT, {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            const isSuccess = response.ok && (
                result.status === 1 ||
                result.Status === 1 ||
                result.status === "success" ||
                result.Status === "Success" ||
                result.d?.IsInserted ||
                result.d === "Success"
            );

            if (isSuccess) {
                showOperationSuccess(`Appointment ${isEdit ? 'updated' : 'added'} successfully.`);
            } else {
                showAlert('error', 'Error', `Failed to ${isEdit ? 'update' : 'add'} appointment. Please try again.`);
            }
        } catch (error) {
            console.error("Error saving appointment:", error);
            showAlert('error', 'Error', "An error occurred while saving the appointment.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-lg w-full max-w-5xl max-h-[100vh] overflow-y-auto">
            <style>
                {`
                    .pac-container {
                        z-index: 99999999 !important;
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                        margin-top: 4px;
                    }
                    .pac-item {
                        padding: 10px 14px;
                        cursor: pointer;
                    }
                    .pac-item:hover {
                        background-color: #f8fafc;
                    }
                `}
            </style>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-6 gap-x-6 gap-y-5">
                {/* Title */}
                <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-gray-700">
                        Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Enter Title"
                        className="premium-input"
                    />
                </div>



                {/* Description */}
                <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        placeholder="Start Typing the details about the appointment"
                        rows={4}
                        className="premium-input !h-auto py-3"
                    />
                </div>

                {/* Remarks */}
                <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Remarks
                    </label>
                    <textarea
                        value={formData.remarks}
                        onChange={(e) => handleChange('remarks', e.target.value)}
                        placeholder="about the task..."
                        rows={4}
                        className="premium-input !h-auto py-3"
                    />
                </div>

                {/* Start Date and Time */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date and Time <span className="text-red-500">*</span>
                    </label>
                    <div className="premium-datetime-container" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                        <Calendar size={18} className="datetime-icon" />
                        <div className="premium-datetime-value">
                            {formatDateTimeDisplay(formData.startDateTime)}
                        </div>
                        <input
                            type="datetime-local"
                            value={formData.startDateTime}
                            min={getLocalISO()}
                            onChange={(e) => handleChange('startDateTime', e.target.value)}
                            className="hidden-datetime-input"
                        />
                    </div>
                </div>

                {/* End Date and Time */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date and Time <span className="text-red-500">*</span>
                    </label>
                    <div className="premium-datetime-container" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                        <Calendar size={18} className="datetime-icon" />
                        <div className="premium-datetime-value">
                            {formatDateTimeDisplay(formData.endDateTime)}
                        </div>
                        <input
                            type="datetime-local"
                            value={formData.endDateTime}
                            min={getLocalISO()}
                            onChange={(e) => handleChange('endDateTime', e.target.value)}
                            className="hidden-datetime-input"
                        />
                    </div>
                </div>

                {/* Where */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Where <span className="text-red-500">*</span>
                    </label>
                    <input
                        ref={whereInputRef}
                        type="text"
                        value={formData.where}
                        onChange={(e) => handleChange('where', e.target.value)}
                        placeholder="Enter location of appointment"
                        className="premium-input"
                    />
                </div>

                {/* Related To */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Related To <span className="text-red-500">*</span>
                    </label>
                    <div className="related-search-container relative">
                        <div className="relative">
                            <input
                                type="text"
                                value={entityName}
                                onChange={(e) => {
                                    setEntityName(e.target.value);
                                    setEntityId('0');
                                    fetchRelatedToSuggestions(e.target.value, entityType);
                                }}
                                onFocus={() => setShowRelatedDropdown(true)}
                                placeholder="Type Task name or Mobile or Company"
                                className="premium-input"
                            />
                        </div>

                        {showRelatedDropdown && relatedSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full top-[calc(100%+4px)] bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                                {relatedSuggestions.map((s, idx) => (
                                    <div
                                        key={idx}
                                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 flex items-center gap-3"
                                        onClick={() => {
                                            setEntityName(s.Name || s.Text || '');
                                            setEntityId(s.Id || s.Code || '');
                                            setShowRelatedDropdown(false);
                                        }}
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden border border-gray-200">
                                            {s.Image ? (
                                                <img src={s.Image} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-800 truncate">{s.Name || s.Text}</div>
                                            <div className="text-[11px] text-gray-500 flex items-center gap-3 mt-1 truncate">
                                                {s.Mobile && <span className="flex items-center gap-1"><PhoneCall size={10} /> {s.Mobile}</span>}
                                                <span className="flex items-center gap-1"><MapPin size={10} /> {s.City || 'Select Country'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Complete Address */}
                <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Complete Address
                    </label>
                    <textarea
                        value={formData.completeAddress}
                        onChange={(e) => handleChange('completeAddress', e.target.value)}
                        placeholder="Please eneter complete address."
                        rows={4}
                        className="premium-input !h-auto py-3"
                    />
                </div>

                {/* Owner */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={formData.owner}
                        onChange={(e) => handleChange('owner', e.target.value)}
                        className="premium-select appearance-none bg-white cursor-pointer"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.75rem center',
                            backgroundSize: '1.5rem',
                            paddingRight: '2.5rem'
                        }}
                    >
                        {ownerOptions.map((opt, index) => (
                            <option key={index} value={opt.User_ID || opt.UserId || opt.Code || opt.ID || opt.Id || opt.Value}>{opt.Name || opt.UserName || opt.Text || opt.DisplayName || opt.FullName}</option>
                        ))}
                    </select>
                </div>

                {/* Collaborators */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Collaborators
                    </label>
                    <div className="relative">
                        <div
                            onClick={() => setShowCollaborators(!showCollaborators)}
                            className="premium-input !h-auto min-h-[46px] py-2 flex flex-wrap gap-2 items-center cursor-pointer"
                        >
                            {formData.collaborators.length > 0 ? (
                                formData.collaborators.map(val => {
                                    const opt = collabOptions.find(o => (o.Code || o.Value || o.Id || o.UserId) === val);
                                    return (
                                        <span key={val} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm text-gray-700 border border-gray-200">
                                            {opt?.Text || opt?.Name || opt?.UserName || val}
                                            <X size={14} className="cursor-pointer hover:text-red-500" onClick={(e) => { e.stopPropagation(); handleCollaboratorToggle(val); }} />
                                        </span>
                                    );
                                })
                            ) : (
                                <span className="text-gray-400">Select collaborators</span>
                            )}
                            <div className="flex-1"></div>
                            <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ transition: 'transform 0.2s ease', transform: showCollaborators ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>

                        {/* Collaborators Dropdown */}
                        {showCollaborators && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                {/* Header */}
                                <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-2 font-medium sticky top-0">
                                    ---Select Collaborators---
                                </div>
                                {/* Options */}
                                <div className="py-1">
                                    {collabOptions.map((opt, index) => {
                                        const val = opt.Code || opt.Value || opt.Id || opt.UserId;
                                        const text = opt.Text || opt.Name || opt.UserName;
                                        return (
                                            <label
                                                key={index}
                                                className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={formData.collaborators.includes(val)}
                                                    onChange={() => handleCollaboratorToggle(val)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">{text}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </form>

            {/* Footer Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex justify-end gap-3 mt-4">
                <button
                    type="button"
                    onClick={handleReset}
                    className="px-8 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                >
                    Reset
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-sm transition-all"
                >
                    SAVE
                </button>
            </div>

            {/* Centralized Alert */}
            <Alert
                {...alertConfig}
                onConfirm={handleAlertClose}
                onCancel={() => setAlertConfig(prev => ({ ...prev, show: false }))}
            />
        </div>
    );
};

export default AddAppointment;
